import { useCallback } from 'react';
import { visitsRequests } from 'redux/requests/visits.request';
import { useApiCall } from 'redux/utils/apiHook.hook';
import type { NoteFieldSaveOverride } from '../NoteField';
import { useParentLinkedNoteSaveOverride } from './useParentLinkedNoteSaveOverride.hook';

/** Params for {@link useVisitNoteSaveOverride}. */
export interface UseVisitNoteSaveOverrideParams {
  visitId?: number;
  /** When false, NoteField uses its default persistence path. */
  enabled: boolean;
}

/**
 * Debounced visit note autosave via parent PATCH (`updateVisit` with `{ note }`).
 * Uses a dedicated API hook so manual full saves do not share autosave state.
 *
 * @param params - Visit id and whether edit-mode override is active.
 * @returns Save override for {@link NoteField}.
 */
export function useVisitNoteSaveOverride({
  visitId,
  enabled,
}: UseVisitNoteSaveOverrideParams): {
  visitNoteSaveOverride: NoteFieldSaveOverride | undefined;
} {
  const [
    _noteAutosaveResult,
    isNoteAutosaving,
    saveVisitNoteOnly,
    noteAutosaveError,
  ] = useApiCall(visitsRequests.updateVisit);

  const saveVisitNoteViaParent = useCallback(
    (text: string) => {
      if (visitId == null) {
        return;
      }
      saveVisitNoteOnly({
        params: { visitId },
        body: { note: text },
      });
    },
    [saveVisitNoteOnly, visitId]
  );

  const visitNoteSaveOverride = useParentLinkedNoteSaveOverride({
    enabled: enabled && visitId != null,
    onSaveNote: saveVisitNoteViaParent,
    isSaving: isNoteAutosaving,
    error: noteAutosaveError,
  });

  return { visitNoteSaveOverride };
}
